import { imu2AdSlotSizesQuickNews } from "@config/AdsConstants";
import { CompanionAdsImu2 } from "@elements/Advertisement/variant/CompanionAdImu";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { AdsCardProps } from "@typings/QuickNews";
import { ReactElement, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

type QNAdCardProps = {
  item: AdsCardProps;
  onNext: () => void;
  isMobile: boolean;
};

export default function QnAdCard({
  item,
  onNext,
  isMobile,
}: QNAdCardProps): ReactElement {
  const canvasId = `qn-ad-read-more-${item.id}`;
  const lottieInstanceRef = useRef<LottieInstance | null>(null);
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  // Memoize IMU slot mapping
  const imu2AdSlots = useMemo(
    () =>
      isMobile
        ? imu2AdSlotSizesQuickNews.mobile
        : imu2AdSlotSizesQuickNews.desktop,
    [isMobile],
  );

  useEffect(() => {
    const isActive = { current: true };

    const config: LottieConfig = {
      animationPath: "quick-news/readMore.lottie",
      canvasId,
      dimensions: {
        height: 29,
        minHeight: 29,
      },
    };

    const instance = loadLottieAnimation(config);
    if (isActive.current && instance) {
      lottieInstanceRef.current = instance;
    }

    return () => {
      isActive.current = false;
      if (lottieInstanceRef.current) {
        lottieInstanceRef.current.destroy();
        lottieInstanceRef.current = null;
      }
    };
  }, [canvasId]);

  return (
    <div
      key={item.id}
      className="w-full h-full bg-transparent md:bg-black-100/10"
      style={{ touchAction: "pan-y" }}
    >
      <CompanionAdsImu2
        uniqueSectionName="quick-news"
        pageAdTargetType={pageAdTargetValue}
        pageNumber={item.adIndex || 1}
        rootClassName="h-full my-auto lg:h-full"
        subRootClassName="relative flex h-full my-auto items-center justify-center lg:relative"
        overrideImu2AdSlots={imu2AdSlots}
      />

      <button
        aria-label="Next card"
        onClick={onNext}
        className="absolute bottom-4 w-full text-white-100 flex flex-col items-center"
      >
        மேலும் செய்திகளுக்கு நகர்த்தவும்
        <canvas id={canvasId} className={twMerge("mx-auto w-[11px]")} />
      </button>
    </div>
  );
}
